/**
 * Policy Administration API client.
 *
 * Thin HTTP layer over the PAS backend. Every method mirrors the shape the store
 * (and, before it, the mock service) expected — the backend already returns the
 * camelCase domain shapes declared in `types.ts`, so no key transform is needed.
 */

import apiClient from '../../utils/axiosConfig';
import { PAS_ENDPOINTS } from './constants';
import type {
  ApprovedQuote,
  Broker,
  Carrier,
  NewPolicyDraft,
  PasUser,
  Policy,
  PolicyNote,
  UnderwritingDecision,
  WorkflowSettings,
} from './types';

/**
 * Coerce the `/products/` payload into a plain `string[]` of product names.
 *
 * The endpoint may return bare strings, `{ id, name }`-style objects, or a
 * DRF-paginated `{ results: [...] }` envelope. Downstream code (store + the
 * NewPolicyModal picker) expects names as strings, so normalize here at the
 * boundary — anything non-string that we can't turn into a name is dropped
 * rather than left to blow up `String.prototype.localeCompare` in the UI.
 */
function normalizeProductNames(data: unknown): string[] {
  const list = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];

  return list
    .map((item): string => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const name =
          o.name ?? o.label ?? o.title ?? o.product ?? o.productName ?? o.product_name ?? o.display_name;
        if (typeof name === 'string') return name;
      }
      return '';
    })
    .filter((name) => name.length > 0);
}

export const pasService = {
  async list(): Promise<Policy[]> {
    // Pagination is off on the PAS list endpoint, so the body is a bare array.
    const response = await apiClient.get<Policy[]>(PAS_ENDPOINTS.POLICIES);
    return response.data;
  },

  async get(id: string): Promise<Policy | null> {
    const response = await apiClient.get<Policy>(PAS_ENDPOINTS.POLICY(id));
    return response.data;
  },

  async createFromQuote(draft: NewPolicyDraft): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.FROM_QUOTE, draft);
    return response.data;
  },

  async submitToUnderwriting(id: string): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.SUBMIT_TO_UNDERWRITING(id));
    return response.data;
  },

  async setChecklistItem(id: string, itemId: string, done: boolean): Promise<Policy> {
    const response = await apiClient.patch<Policy>(PAS_ENDPOINTS.CHECKLIST_ITEM(id, itemId), { done });
    return response.data;
  },

  async recordDecision(
    id: string,
    decision: UnderwritingDecision['decision'],
    rationale: string,
  ): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.UNDERWRITING_DECISIONS(id), {
      decision,
      rationale,
    });
    return response.data;
  },

  async generateBinder(id: string): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.GENERATE_BINDER(id));
    return response.data;
  },

  async issuePolicy(id: string): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.ISSUE(id));
    return response.data;
  },

  async activatePolicy(id: string): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.ACTIVATE(id));
    return response.data;
  },

  async cancelPolicy(id: string, reason: string, note: string, cancellationEndDate: string): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.CANCEL(id), {
      reason,
      note,
      cancellationEndDate,
    });
    return response.data;
  },

  async renewPolicy(
    id: string,
    opts: { effectiveDate: string; annualPremium: number; note: string },
  ): Promise<{ original: Policy; renewal: Policy }> {
    const response = await apiClient.post<{ original: Policy; renewal: Policy }>(
      PAS_ENDPOINTS.RENEW(id),
      opts,
    );
    return response.data;
  },

  async addNote(id: string, body: string, category: PolicyNote['category']): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.NOTES(id), { body, category });
    return response.data;
  },

  async setNotePinned(id: string, noteId: string, pinned: boolean): Promise<Policy> {
    const response = await apiClient.patch<Policy>(PAS_ENDPOINTS.NOTE(id, noteId), { pinned });
    return response.data;
  },

  async addDocumentVersion(id: string, docId: string, note: string): Promise<Policy> {
    const response = await apiClient.post<Policy>(PAS_ENDPOINTS.REGENERATE_DOCUMENT(id, docId), { note });
    return response.data;
  },

  // --- Reference data --------------------------------------------------------

  async listBrokers(): Promise<Broker[]> {
    const response = await apiClient.get<Broker[]>(PAS_ENDPOINTS.BROKERS);
    return response.data;
  },

  async listCarriers(): Promise<Carrier[]> {
    const response = await apiClient.get<Carrier[]>(PAS_ENDPOINTS.CARRIERS);
    return response.data;
  },

  async listUsers(): Promise<PasUser[]> {
    const response = await apiClient.get<PasUser[]>(PAS_ENDPOINTS.USERS);
    return response.data;
  },

  async listProducts(): Promise<string[]> {
    const response = await apiClient.get<unknown>(PAS_ENDPOINTS.PRODUCTS);
    return normalizeProductNames(response.data);
  },

  async getWorkflowSettings(): Promise<WorkflowSettings> {
    const response = await apiClient.get<WorkflowSettings>(PAS_ENDPOINTS.WORKFLOW_SETTINGS);
    return response.data;
  },

  async listApprovedQuotes(): Promise<ApprovedQuote[]> {
    const response = await apiClient.get<ApprovedQuote[]>(PAS_ENDPOINTS.APPROVED_QUOTES);
    return response.data;
  },
};
